import { CoralogixBrowserSdkConfig, CoralogixDomain, CoralogixRum, UrlBlueprinter } from '@coralogix/browser'
import { getLogger } from 'app/components/logger'
import { ApplicationConfiguration } from '../../types'
import { User } from '../../../auth/types'
import { userStorage } from 'app/modules/auth/helper/storage'
import { monitoringIgnoreUrls } from './monitoring-context'

let isCoralogixActivated = false
const logger = getLogger('RUM:')

const maskUploadsUrl: UrlBlueprinter = (url) =>
  url
    .replace(/\/uploads\/[^/?#]+\/reports\/[^/?#]+/, '/uploads/{flow_id}/reports/{report_id}')
    .replace(/\/uploads\/[^/?#]+/, '/uploads/{flow_id}')
    .replace(/\/search-result\?[^#]+/, '/search-result?{query}')

const urlBlueprinters = {
  pageUrlBlueprinters: [maskUploadsUrl],
}

// Init Coralogix RUM
export function initCoralogix(config?: ApplicationConfiguration['coralogix'], version?: string | null) {
  if (!config) {
    toggleCoralogix()
    return
  }
  if (isCoralogixActivated) {
    logger.debug('Coralogix already activated')
    return
  }

  const settings: CoralogixBrowserSdkConfig = {
    public_key: config.publicKey,
    application: config.appName,
    version: version ?? config.appVersion,
    coralogixDomain: config.domain as CoralogixDomain,
    ignoreUrls: monitoringIgnoreUrls,
    trackSoftNavigations: true,
    urlBlueprinters,
  }

  const settingsValid = validateInitSettings(settings)
  toggleCoralogix(settingsValid ? settings : undefined)
}

// Set the Coralogix user context
export function setCoralogixUser(user: User | undefined) {
  if (!isCoralogixActivated) {
    return
  }

  const clientId = userStorage.getClientId() ?? userStorage.setClientId()
  const userId = user ? user.accountId : clientId

  const meta = user
    ? {
        role: user.isAdmin ? 'admin' : user.isAuditor ? 'auditor' : 'user',
      }
    : undefined

  const settings = {
    user_id: userId,
    user_name: user ? user.username : 'guest',
    user_email: user ? user.email : undefined,
    user_metadata: meta,
  }

  logger.debug('Use coralogix monitoring for user: ', settings)
  CoralogixRum.setUserContext(settings)
}

// Either activate or disable Coralogix
function toggleCoralogix(settings?: CoralogixBrowserSdkConfig) {
  isCoralogixActivated = !!settings

  if (settings) {
    logger.debug('Initialize Coralogix monitoring using settings: ', settings)
    CoralogixRum.init(settings)
  } else {
    logger.warn('Shut down Coralogix monitoring, if it was enabled')
    CoralogixRum.shutdown()
  }
}

// Check if init settings are all set
function validateInitSettings(settings: CoralogixBrowserSdkConfig) {
  for (const name in settings) {
    if (!settings[name as keyof typeof settings]) {
      logger.warn(`Can not initialize Coralogix monitoring, value for '${name}' setting is missing`)
      return false
    }
  }

  return true
}
